import { Formik } from "formik";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { isGuestAccountActive, newGetInitialValues, newValidate, Translation as T, useAmsSelector } from "@awe-web/shared/lib";

import { Button } from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { TypeArea } from "../../../components/layout.js";
import {
  ScreenHeading,
  ScreenLoading,
  ScreenSection,
  ScreenSectionSubheading,
} from "../../../components/screen.js";
import SelectedRentObject from "../../../rent_objects/components/selected_rent_object.jsx";
import { B2C_LTR_SelectProvisioning, B2C_SignUpGuest } from "../../../routing/routes.js";
import SelectedPeriod from "../../../stations/components/selected_period.jsx";
import { useInitialize } from "../../../util/react.js";
import { initialize, submit } from "./actions.js";
import SubmitRentContractForm from "./submit_rent_contract_form.jsx";

import "./submit_rent_contract.scss";

const OVERVIEW = "FSD0000159";
const SELECTED_RENT_OBJECT = "FSD0000162";
const SELECTED_TIME = "ALG0000092";
const CHANGE_VALUES = "ALG0000025";

function InternalSubmitRentContract({ onSubmit }) {

  const validate = useAmsSelector((state) => newValidate("submitB2cLtrRentContract")(state));

  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("submitB2cLtrRentContract");
    const inialValuesFactory = formInitializer(state);
    return inialValuesFactory({
      contractNote: "",
      creditCardId: null,
      agbAccepted: false,
      privacyAccepted: false
    });
  });

  return (
    <Formik validate={validate} initialValues={initialValues} onSubmit={onSubmit}>
      {(formikProps) => (
        <SubmitRentContractForm {...formikProps} />
      )}
    </Formik>
  );
}

export default function B2C_LTR_SubmitRentContract() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isGuestAccount = useSelector(state => isGuestAccountActive(state));

  const onSubmit = useCallback(async (values) => {
    const nextPath = await dispatch(submit(values));
    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const onBackClickHandler = useCallback(() => {
    if (isGuestAccount) {
      navigate(B2C_SignUpGuest.path(), { state: { from: location } });
    }
    else {
      navigate(B2C_LTR_SelectProvisioning.path());
    }
  }, [isGuestAccount, navigate, location]);

  const init = useCallback(async () => await dispatch(initialize()), [dispatch]);
  const isReady = useInitialize(init);
  if (!isReady) {
    return <ScreenLoading />;
  }

  return (
    <div className="b2c-ltr-submit-rent-contract">
      <ScreenSection dark>
        <TypeArea>
          <Row>
            <Col desktop={{ cols: 8, offset: 2 }}>
              <ScreenHeading><T code={OVERVIEW} /></ScreenHeading>
              <div className="b2c-str-submit-rent-contract__selected-rent-object">
                <ScreenSectionSubheading>
                  <T code={SELECTED_RENT_OBJECT} />
                </ScreenSectionSubheading>
                <SelectedRentObject selectedBasicProperties="B2C" />
              </div>

              <div>
                <ScreenSectionSubheading>
                  <T code={SELECTED_TIME} />
                </ScreenSectionSubheading>
                <SelectedPeriod
                  className="b2c-str-submit-rent-contract__selected-period"
                  editButtonLocation={!isGuestAccount ? B2C_LTR_SelectProvisioning.path() : B2C_SignUpGuest.path()}
                />
                <Button className="b2c-str-submit-rent-contract__mobile-back-button" secondary back
                  onClick={onBackClickHandler}
                >
                  {<T code={CHANGE_VALUES} />}
                </Button>
              </div>
            </Col>
          </Row>
        </TypeArea>
      </ScreenSection>

      <InternalSubmitRentContract onSubmit={onSubmit} />
    </div>
  );
}
